import React from 'react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';

/**
 * Segmented, the committed choice among a small set.
 *
 * Source selectors: `system.css` `.dsk-segmented`, `--block`, and the
 * `button[aria-pressed="true"]` / `[aria-checked="true"]` / `.is-active`
 * selected rules. It is the generalised form of `.dsk-viewtoggle`.
 *
 * Doctrine enforced:
 * - A segmented control is a COMMITTED choice, so the chosen segment fills with
 *   INK, matching `.dsk-chip.is-active`. Not accent: accent marks a state the
 *   system is reporting, ink marks a choice the user made.
 * - Selection is reported with `aria-pressed`, the toolbar semantic the CSS
 *   lists first and the one `.dsk-viewtoggle` already uses, so keyboard users
 *   Tab through real buttons rather than a hand-rolled roving tabindex.
 * - The active segment also gets a filled icon where one is supplied, because
 *   fill is the active weight. Never fill on hover.
 * - Inside a `Field` the CSS opts this out of the column stretch, so use
 *   `block` when full width is genuinely what you want.
 *
 *   <Segmented
 *     label="View"
 *     value={view}
 *     onChange={setView}
 *     items={[{ value: 'grid', label: 'Grid', icon: SquaresFour },
 *             { value: 'list', label: 'List', icon: Rows }]}
 *   />
 */
export const Segmented = React.forwardRef(function Segmented(
  { items = [], value, onChange, block = false, label, children, className, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      role="group"
      aria-label={label}
      className={cn('dsk-segmented', block && 'dsk-segmented--block', className)}
      {...rest}
    >
      {children ||
        items.map((item) => {
          const active = item.value === value;
          return (
            <button
              key={item.value}
              type="button"
              aria-pressed={active}
              onClick={() => onChange && onChange(item.value, item)}
            >
              {item.icon ? <Icon as={item.icon} active={active} /> : null}
              {item.label}
            </button>
          );
        })}
    </div>
  );
});
Segmented.displayName = 'Segmented';

export default Segmented;
